import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.dev.mondoway.com";
const APP_DEEP_LINK = process.env.REACT_APP_APP_DEEP_LINK || "mondoway-user://referral";
const CLICK_TIMEOUT_MS = 1200;
const STORE_FALLBACK_MS = 1800;
const RECEIVER_TYPES = ["User", "Business", "Driver"];

const APP_CONFIG = {
  User: {
    label: "Mondoway",
    buttonLabel: "User app",
    androidPackageName:
      process.env.REACT_APP_ANDROID_PACKAGE_NAME || "com.mondowy_app_user",
    androidStoreUrl:
      process.env.REACT_APP_ANDROID_STORE_URL ||
      "https://play.google.com/store/apps/details?id=com.mondowy_app_user",
    androidMarketUrl:
      process.env.REACT_APP_ANDROID_MARKET_URL ||
      "market://details?id=com.mondowy_app_user",
    iosStoreUrl:
      process.env.REACT_APP_IOS_STORE_URL ||
      "https://apps.apple.com/us/app/mondoway/id6444758076"
  },
  Business: {
    label: "Mondoway Business",
    buttonLabel: "Business app",
    androidPackageName: "com.mondowy_app_bussniss",
    androidStoreUrl:
      "https://play.google.com/store/apps/details?id=com.mondowy_app_bussniss",
    iosStoreUrl: ""
  },
  Driver: {
    label: "Mondoway Delivery",
    buttonLabel: "Delivery app",
    androidPackageName: "com.mondowy.delivery",
    androidStoreUrl:
      "https://play.google.com/store/apps/details?id=com.mondowy.delivery",
    iosStoreUrl:
      "https://apps.apple.com/us/app/mondoway-delivery/id6477737266"
  }
};

function getDeviceType() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || "";
  const isAndroid = /android/i.test(userAgent);
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isAndroid) {
    return "android";
  }

  if (isIOS) {
    return "ios";
  }

  return "other";
}

function appendReferralCode(url, referralCode) {
  if (!referralCode || !url) {
    return url;
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}code=${encodeURIComponent(referralCode)}`;
}

function appendQueryParams(url, params) {
  if (!url) {
    return url;
  }

  const [baseUrl, hash] = url.split("#");
  const separator = baseUrl.includes("?") ? "&" : "?";
  const query = Object.entries(params)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  if (!query) {
    return url;
  }

  return `${baseUrl}${separator}${query}${hash ? `#${hash}` : ""}`;
}

function buildAndroidStoreUrl(referralCode, baseUrl = APP_CONFIG.User.androidStoreUrl) {
  const referrer = referralCode ? `code=${referralCode}` : "";

  return appendQueryParams(baseUrl, {
    referrer
  });
}

function buildIOSStoreUrl(referralCode, baseUrl = APP_CONFIG.User.iosStoreUrl) {
  return appendQueryParams(baseUrl, {
    ct: referralCode ? `referral_${referralCode}` : "",
    referralCode
  });
}

function buildAndroidIntentUrl(referralCode) {
  const androidPackageName = APP_CONFIG.User.androidPackageName;
  const fallbackUrl = encodeURIComponent(buildAndroidStoreUrl(referralCode));
  const codeParam = referralCode
    ? `?code=${encodeURIComponent(referralCode)}`
    : "";

  return `intent://referral${codeParam}#Intent;scheme=mondoway;package=${androidPackageName};S.browser_fallback_url=${fallbackUrl};end`;
}

function openUrl(url) {
  if (url) {
    window.location.href = url;
  }
}

function getAndroidMarketUrl(receiverType) {
  return (
    APP_CONFIG[receiverType].androidMarketUrl ||
    `market://details?id=${APP_CONFIG[receiverType].androidPackageName}`
  );
}

function getStoreUrl(receiverType, deviceType, referralCode, options = {}) {
  const appConfig = APP_CONFIG[receiverType] || APP_CONFIG.User;

  if (deviceType === "ios") {
    return appConfig.iosStoreUrl
      ? buildIOSStoreUrl(referralCode, appConfig.iosStoreUrl)
      : "";
  }

  if (deviceType === "android") {
    return buildAndroidStoreUrl(
      referralCode,
      options.useMarketUrl ? getAndroidMarketUrl(receiverType) : appConfig.androidStoreUrl
    );
  }

  return buildAndroidStoreUrl(referralCode, appConfig.androidStoreUrl);
}

function getUniqueReceiverTypes(campaign) {
  const receiverTypes = Array.isArray(campaign?.receiverTypes)
    ? campaign.receiverTypes
    : [];
  const campaignReceiverTypes = Array.isArray(campaign?.activeCampaigns)
    ? campaign.activeCampaigns.map((activeCampaign) => activeCampaign.receiverType)
    : [];
  const allReceiverTypes = [...receiverTypes, ...campaignReceiverTypes];

  return RECEIVER_TYPES.filter((receiverType) =>
    allReceiverTypes.includes(receiverType)
  );
}

async function fetchActiveInviteCampaign(inviteCode) {
  const campaignUrl = new URL(
    `${API_BASE_URL}/v1/user-app/referral/active-invite-campaign`
  );
  campaignUrl.searchParams.set("sender", inviteCode);

  const response = await fetch(campaignUrl.toString());

  if (!response.ok) {
    throw new Error("Unable to load referral campaign");
  }

  return response.json();
}

function ReferralRedirect() {
  const location = useLocation();
  const fallbackTimerRef = useRef();
  const [status, setStatus] = useState("Checking referral link...");
  const [helperText, setHelperText] = useState(
    "Please wait while we find the right Mondoway app."
  );
  const [isLoading, setIsLoading] = useState(true);
  const [selectableReceiverTypes, setSelectableReceiverTypes] = useState([]);
  const [resolvedReferralCode, setResolvedReferralCode] = useState("");

  const inviteCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      params.get("code") ||
      params.get("referralCode") ||
      params.get("sender") ||
      ""
    );
  }, [location.search]);

  const clearStoreFallback = useCallback(() => {
    window.clearTimeout(fallbackTimerRef.current);
  }, []);

  const recordInviteClick = useCallback(async (referralCode) => {
    if (!referralCode) {
      return;
    }

    const clickUrl = `${API_BASE_URL}/v1/user-app/referral/invite-click?referralCode=${encodeURIComponent(
      referralCode
    )}`;

    const request = fetch(clickUrl, {
      method: "POST",
      keepalive: true
    }).catch(() => undefined);

    const timeout = new Promise((resolve) => {
      window.setTimeout(resolve, CLICK_TIMEOUT_MS);
    });

    await Promise.race([request, timeout]);
  }, []);

  const redirectToReceiverType = useCallback(
    async (receiverType, referralCode, options = {}) => {
      const appConfig = APP_CONFIG[receiverType] || APP_CONFIG.User;
      const deviceType = getDeviceType();

      clearStoreFallback();
      setSelectableReceiverTypes([]);
      setIsLoading(false);

      if (deviceType === "ios" && receiverType === "Business") {
        setStatus("Mondoway Business is not available for iPhone yet.");
        setHelperText(
          "Open this referral link on an Android device to install the Business app."
        );
        return;
      }

      setStatus(`Opening ${appConfig.label}...`);
      setHelperText(
        "If nothing happens, install the app from your phone store and open this link again."
      );

      await recordInviteClick(referralCode);

      if (options.useCurrentUserFallback) {
        const deepLinkUrl =
          deviceType === "android"
            ? buildAndroidIntentUrl(referralCode)
            : appendReferralCode(APP_DEEP_LINK, referralCode);
        const storeUrl =
          deviceType === "ios"
            ? buildIOSStoreUrl(referralCode)
            : deviceType === "android"
              ? getStoreUrl("User", deviceType, referralCode, {
                  useMarketUrl: true
                })
              : buildAndroidStoreUrl(referralCode);

        if (deviceType === "other") {
          openUrl(buildAndroidStoreUrl(referralCode));
          return;
        }

        openUrl(deepLinkUrl);

        fallbackTimerRef.current = window.setTimeout(() => {
          if (storeUrl) {
            openUrl(storeUrl);
          } else {
            setStatus("Mondoway store link is not configured yet.");
          }
        }, STORE_FALLBACK_MS);
        return;
      }

      const storeUrl = getStoreUrl(receiverType, deviceType, referralCode);

      if (storeUrl) {
        openUrl(storeUrl);
      } else {
        setStatus(`${appConfig.label} store link is not available yet.`);
        setHelperText("Please try again later or use a supported device.");
      }
    },
    [clearStoreFallback, recordInviteClick]
  );

  useEffect(() => {
    let isMounted = true;

    const resolveReferralDestination = async () => {
      clearStoreFallback();
      setIsLoading(true);
      setSelectableReceiverTypes([]);
      setStatus("Checking referral link...");
      setHelperText("Please wait while we find the right Mondoway app.");

      if (!inviteCode) {
        await redirectToReceiverType("User", "", {
          useCurrentUserFallback: true
        });
        return;
      }

      try {
        const campaign = await fetchActiveInviteCampaign(inviteCode);

        if (!isMounted) {
          return;
        }

        const receiverTypes = getUniqueReceiverTypes(campaign);
        const nextReferralCode = campaign?.referralCode || inviteCode;
        setResolvedReferralCode(nextReferralCode);

        if (!campaign?.hasActiveCampaign || receiverTypes.length === 0) {
          await redirectToReceiverType("User", nextReferralCode, {
            useCurrentUserFallback: true
          });
          return;
        }

        if (receiverTypes.length === 1) {
          await redirectToReceiverType(receiverTypes[0], nextReferralCode);
          return;
        }

        setIsLoading(false);
        setSelectableReceiverTypes(receiverTypes);
        setStatus("Choose your Mondoway app");
        setHelperText("Select the app that matches how you want to use this invitation.");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        await redirectToReceiverType("User", inviteCode, {
          useCurrentUserFallback: true
        });
      }
    };

    resolveReferralDestination();

    return () => {
      isMounted = false;
      clearStoreFallback();
    };
  }, [clearStoreFallback, inviteCode, redirectToReceiverType]);

  const handleAppSelection = (receiverType) => {
    redirectToReceiverType(receiverType, resolvedReferralCode || inviteCode);
  };

  return (
    <section className="min-h-[60vh] bg-[#FAF8F2] px-4 py-20 text-center">
      <div className="mx-auto max-w-md">
        <img
          src="/icons/new_login_logo.svg"
          alt="Mondoway Logo"
          className="mx-auto mb-8 h-16 w-auto"
        />
        {isLoading && (
          <div className="mx-auto mb-5 h-8 w-8 animate-spin rounded-full border-4 border-[#D8B4FE] border-t-[#6A0DAD]" />
        )}
        <h1 className="mb-3 text-2xl font-bold text-[#0F172A]">
          {status}
        </h1>
        <p className="text-[#64748B]">
          {helperText}
        </p>
        {selectableReceiverTypes.length > 1 && (
          <div className="mt-8 grid gap-3">
            {selectableReceiverTypes.map((receiverType) => (
              <button
                key={receiverType}
                type="button"
                onClick={() => handleAppSelection(receiverType)}
                className="w-full rounded-lg border border-[#D8B4FE] bg-white px-5 py-4 text-left font-semibold text-[#0F172A] shadow-sm transition-colors hover:bg-[#F4E8FF]"
              >
                <span className="block text-base">
                  {APP_CONFIG[receiverType].buttonLabel}
                </span>
                <span className="mt-1 block text-sm font-normal text-[#64748B]">
                  {APP_CONFIG[receiverType].label}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default ReferralRedirect;
